import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@core/components/ui/dialog";
import { Button } from "@core/components/ui/button";
import { Textarea } from "@core/components/ui/textarea";
import { useToast } from "@core/hooks/use-toast";
import { useAuth } from "@core/contexts/AuthContext";
import { GroupSelector } from "@core/components/groups/GroupSelector";
import { collectInviteEmails } from "@core/lib/inviteEmails";
import {
  createPollInvitations,
  listPollInvitations,
  sendPollInvitations,
} from "@core/services/InvitationService";
import { pollService } from "@core/services/PollService";
import { AutoRemindersToggle } from "@core/components/invitations/AutoRemindersToggle";
import {
  getPollAutoReminders,
  setPollAutoReminders,
} from "@core/services/InvitationService";
import { hasPaidAccess } from "@core/constants/quotas";
import { logger } from "@core/lib/logger";

interface AddInviteesDialogProps {
  pollId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Mode d'audience enregistré du sondage. En mode « link » (toute personne
   * avec le lien), un message explique ce que change l'ajout d'invités.
   */
  audienceMode?: string;
  /** Appelé après un ajout réussi, avec le nombre d'invitations envoyées. */
  onAdded?: (sentCount: number) => void;
}

/**
 * Ajouter des invités à un sondage DÉJÀ publié (décisions Julien, 2026-09-16) :
 * - adresses saisies ou groupe enregistré ;
 * - les adresses déjà invitées sont signalées et IGNORÉES — les réinviter
 *   générerait un nouveau lien et invaliderait l'ancien (décision « même
 *   lien ») ; pour renvoyer le mail, il y a la relance ;
 * - envoi immédiat, aux seules nouvelles adresses ;
 * - sur un sondage « toute personne avec le lien », message explicatif avant
 *   confirmation : le lien public reste valable, on ajoute des destinataires.
 *
 * Monté sur la page Résultats (créateur), l'écran de modification et la page
 * Invitations.
 */
export const AddInviteesDialog: React.FC<AddInviteesDialogProps> = ({
  pollId,
  open,
  onOpenChange,
  audienceMode,
  onAdded,
}) => {
  const { t } = useTranslation("polls");
  const { toast } = useToast();
  const { user, profile } = useAuth();
  const [raw, setRaw] = useState("");
  const [group, setGroup] = useState<{ id: string; name: string } | null>(null);
  const [existing, setExisting] = useState<Set<string>>(new Set());
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [existingError, setExistingError] = useState(false);
  const [sending, setSending] = useState(false);

  // Invités existants, relus à chaque ouverture (la liste a pu changer).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingExisting(true);
    setExistingError(false);
    listPollInvitations(pollId)
      .then((rows) => {
        if (!cancelled) {
          setExisting(new Set(rows.map((r) => r.email.toLowerCase())));
        }
      })
      .catch(() => {
        // Sans la liste, on ne peut pas repérer les doublons : le serveur
        // ferait tourner leur jeton. On bloque l'envoi plutôt que de risquer
        // d'invalider des liens déjà envoyés (cf. canSubmit).
        if (!cancelled) {
          setExisting(new Set());
          setExistingError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingExisting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, pollId]);

  // Réinitialise le formulaire à la fermeture.
  useEffect(() => {
    if (!open) {
      setRaw("");
      setGroup(null);
    }
  }, [open]);

  // Relance automatique (Solo) : proposée ICI, au moment où l'on ajoute des
  // invités — c'est là que la question se pose. Modifiable ensuite sur la
  // page Invitations.
  const autoRemindersLocked = !user || !hasPaidAccess(profile);
  const [autoReminders, setAutoReminders] = useState(false);
  const [savingAutoReminders, setSavingAutoReminders] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void getPollAutoReminders(pollId).then((enabled) => {
      if (!cancelled) setAutoReminders(enabled);
    });
    return () => {
      cancelled = true;
    };
  }, [open, pollId]);

  const handleAutoRemindersChange = async (next: boolean) => {
    setSavingAutoReminders(true);
    setAutoReminders(next);
    try {
      await setPollAutoReminders(pollId, next);
    } catch (error) {
      setAutoReminders(!next);
      const message = error instanceof Error ? error.message : String(error);
      toast({
        title: message.includes("SOLO_PLAN_REQUIRED")
          ? t("invitations.auto_reminders.solo_required")
          : t("invitations.auto_reminders.save_error"),
        variant: "destructive",
      });
    } finally {
      setSavingAutoReminders(false);
    }
  };

  // Chaîne libre → découpée sur virgules / points-virgules / espaces,
  // normalisée en minuscules, adresses invalides écartées.
  const emails = useMemo(
    () => collectInviteEmails({ settings: { participantEmails: raw } }),
    [raw],
  );
  const newEmails = emails.filter((e) => !existing.has(e));
  const alreadyInvited = emails.filter((e) => existing.has(e));
  const isLinkMode = audienceMode === "link" || existing.size === 0;
  const canSubmit =
    newEmails.length > 0 && !loadingExisting && !existingError && !sending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSending(true);
    try {
      const tokens = await createPollInvitations(pollId, newEmails, {
        source: group ? "group" : "manual",
        groupId: group?.id,
      });
      const res = await sendPollInvitations(pollId, tokens);

      if (res.successCount > 0 && res.failedCount === 0) {
        toast({
          title: t("invitations.add.sent_title"),
          description: t("invitations.add.sent_description", {
            count: res.successCount,
          }),
        });
      } else if (res.successCount > 0) {
        toast({
          title: t("invitations.add.sent_title"),
          description: t("invitations.add.partial_description", {
            sent: res.successCount,
            total: tokens.length,
          }),
          variant: "destructive",
        });
      } else {
        toast({
          title: t("invitations.error_title"),
          description: t("invitations.add.failed_description"),
          variant: "destructive",
        });
      }

      // Le sondage passe en « E-mails directs ». Réservé aux comptes : un
      // créateur sans compte ne peut pas modifier `polls` (pas de policy),
      // et ce marqueur n'est qu'informatif — les invitations fonctionnent
      // sans lui. Échec non bloquant.
      if (isLinkMode && user && res.successCount > 0) {
        pollService
          .updatePoll(pollId, { settings: { audienceMode: "emails" } } as never)
          .catch((error: unknown) =>
            logger.warn("audienceMode non mis à jour", "poll", { error }),
          );
      }

      if (res.successCount > 0) {
        onAdded?.(res.successCount);
        onOpenChange(false);
      }
    } catch (error) {
      logger.error("Ajout d'invités échoué", "poll", { error });
      toast({
        title: t("invitations.error_title"),
        description: t("invitations.add.failed_description"),
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="add-invitees-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t("invitations.add.title")}</DialogTitle>
          <DialogDescription>
            {t("invitations.add.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {existingError && (
            <p
              className="text-sm text-destructive"
              data-testid="add-invitees-load-error"
            >
              {t("invitations.add.load_error")}
            </p>
          )}
          {user && (
            <GroupSelector
              currentEmails={emails}
              selectedGroupId={group?.id}
              onSelectGroup={(groupEmails, selected) => {
                setRaw(groupEmails.join(", "));
                setGroup(selected ?? null);
              }}
            />
          )}

          <Textarea
            value={raw}
            onChange={(e) => {
              setRaw(e.target.value);
              // Une saisie libre n'est plus « le groupe » tel quel.
              if (group) setGroup(null);
            }}
            placeholder={t("invitations.add.placeholder")}
            rows={4}
            data-testid="add-invitees-input"
          />

          <AutoRemindersToggle
            checked={autoReminders}
            onChange={handleAutoRemindersChange}
            locked={autoRemindersLocked}
            busy={savingAutoReminders}
            testId="add-invitees-auto-reminders"
          />

          {emails.length > 0 && (
            <div className="space-y-2 text-sm">
              <p className="text-foreground" data-testid="add-invitees-new">
                {newEmails.length > 0
                  ? t("invitations.add.new_count", { count: newEmails.length })
                  : t("invitations.add.no_new")}
              </p>
              {alreadyInvited.length > 0 && (
                <div
                  className="rounded-md border border-border bg-muted px-3 py-2"
                  data-testid="add-invitees-already"
                >
                  <p className="font-medium text-foreground">
                    {t("invitations.add.already_invited", {
                      count: alreadyInvited.length,
                    })}
                  </p>
                  <p className="mt-1 break-all text-muted-foreground">
                    {alreadyInvited.join(", ")}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("invitations.add.already_invited_hint")}
                  </p>
                </div>
              )}
            </div>
          )}

          {isLinkMode && newEmails.length > 0 && (
            <div
              className="flex gap-2 rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-foreground"
              data-testid="add-invitees-link-mode"
            >
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <p>{t("invitations.add.link_mode_notice")}</p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("invitations.add.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="add-invitees-submit"
          >
            {isLinkMode
              ? t("invitations.add.confirm_and_send", {
                  count: newEmails.length,
                })
              : t("invitations.add.send", { count: newEmails.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddInviteesDialog;
